import type { Polarity } from '../types';
import { sampleBlueNoiseBilinear } from '../util/blue-noise-sample';

export interface PatternThresholdOptions {
  /** Threshold offset, 0-100. Default 50 (no shift). */
  density: number;
  /** 'black': source brighter than threshold paints white. 'white': inverted. */
  polarity: Polarity;
  /**
   * Compose the source tone with the pattern tone, then blue-noise
   * dither. `threshold` keeps the v0.2.2 per-pixel threshold compare.
   * `multiply` is v0.2.3's Darken-group compose. Screen / Overlay /
   * Soft Light / Pin Light are added in v0.2.3.1 (Lighten- and
   * Contrast-group blend modes). Default threshold.
   */
  blendMode?: 'threshold' | 'multiply' | 'screen' | 'overlay' | 'softlight' | 'pinlight';
  /** Scale factor applied to the pattern before tiling. Default 1.0. */
  patternScale?: number;
  /** Degrees. Rotates tiled pattern sampling within each repeated tile. Default 0. */
  patternRotationDeg?: number;
  /** Mirrors tiled pattern sampling horizontally. Default false. */
  patternFlipX?: boolean;
  /** Mirrors tiled pattern sampling vertically. Default false. */
  patternFlipY?: boolean;
  /** Source pixels darker than this render solid FG. Default 0.02. */
  blackThreshold?: number;
  /** Source pixels brighter than this render solid BG. Default 0.98. */
  whiteThreshold?: number;
  /** 0..1. Blue-noise modulation added to the pattern threshold. Default 0. */
  patternNoise?: number;
  /** 1..16. Sampling scale for the blue-noise tile (bilinear). Default 1 = per-pixel grain. */
  patternNoiseScale?: number;
  /** Invert pattern luminance before the source compare. Default false (v0.2.1 semantic); v0.2.2 demo flips this default to true. */
  patternInvert?: boolean;
  /** 0..1. Mix factor between a flat 0.5 baseline (0) and the full pattern (1). Default 1. */
  patternOpacity?: number;
}

/**
 * Optional offset added to (x, y) before pattern tile lookup. The
 * multi-region pipeline crops the source to each region's bbox and passes
 * the bbox origin here so tile sampling stays consistent with what a
 * full-image run would produce.
 */
export interface PatternThresholdOrigin {
  originX: number;
  originY: number;
}

/**
 * Per-pixel pattern threshold compare. The pattern tiles via wrap-around with
 * an optional `patternScale` factor (>1 enlarges, <1 shrinks the period). The
 * solid-input clamp via `blackThreshold` / `whiteThreshold` mirrors the
 * Family A clamp, so a Family B render of solid black source stays solid
 * black even with a non-uniform pattern.
 *
 *   isWhite = (gray + densityShift) > pattern
 *   polarity 'black': isWhite -> white pixel, else black
 *   polarity 'white': inverted
 */
export function patternThresholdRender(
  source: ImageData,
  pattern: ImageData,
  options: PatternThresholdOptions,
  origin?: PatternThresholdOrigin,
): ImageData {
  return patternRender(source, pattern, options, origin, false);
}

/**
 * Continuous-tone version of the Custom Pattern compose step. It applies the
 * same source adjustment, pattern blend, opacity, invert, and noise inputs as
 * `patternThresholdRender`, but leaves the final black/white decision to a
 * later renderer such as Family A halftone.
 */
export function patternToneRender(
  source: ImageData,
  pattern: ImageData,
  options: PatternThresholdOptions,
  origin?: PatternThresholdOrigin,
): ImageData {
  return patternRender(source, pattern, options, origin, true);
}

function patternRender(
  source: ImageData,
  pattern: ImageData,
  options: PatternThresholdOptions,
  origin: PatternThresholdOrigin | undefined,
  outputTone: boolean,
): ImageData {
  const w = source.width;
  const h = source.height;
  const pw = pattern.width;
  const ph = pattern.height;
  const out = new ImageData(w, h);
  const src = source.data;
  const pat = pattern.data;
  const densityShift = (50 - options.density) / 50;
  const polarityWhite = options.polarity === 'white';
  const scale = Math.max(0.001, options.patternScale ?? 1);
  const patternRotationDeg = options.patternRotationDeg ?? 0;
  const patternFlipX = options.patternFlipX ?? false;
  const patternFlipY = options.patternFlipY ?? false;
  const blackTh = options.blackThreshold ?? 0.02;
  const whiteTh = options.whiteThreshold ?? 0.98;
  const blendMode = options.blendMode ?? 'threshold';
  const thresholdNoiseAmt = Math.max(0, Math.min(1, options.patternNoise ?? 0));
  const multiplyNoiseAmt = Math.max(0, Math.min(1, options.patternNoise ?? 0));
  const noiseScale = Math.max(1, options.patternNoiseScale ?? 1);
  const invert = options.patternInvert ?? false;
  const opacity = Math.max(0, Math.min(1, options.patternOpacity ?? 1));
  const offX = origin?.originX ?? 0;
  const offY = origin?.originY ?? 0;

  const fgValue = polarityWhite ? 255 : 0;
  const bgValue = polarityWhite ? 0 : 255;

  // For Screen + Pin Light the source extreme can be overridden by the
  // pattern (Screen lifts dark source via pattern brightness; Pin Light
  // clamps both ways). Skip the source-extreme clamps for these modes
  // so the user-uploaded pattern actually does its job on solid-dark
  // (or solid-bright, for Pin Light) source regions.
  const skipSourceClamp = blendMode === 'screen' || blendMode === 'pinlight';

  for (let y = 0; y < h; y++) {
    const fullY = offY + y;
    for (let x = 0; x < w; x++) {
      const fullX = offX + x;
      const i = (y * w + x) * 4;
      const grayRaw = (src[i] ?? 0) / 255;
      let v: number;
      if (!skipSourceClamp && grayRaw <= blackTh) {
        v = fgValue;
      } else if (!skipSourceClamp && grayRaw >= whiteTh) {
        v = bgValue;
      } else {
        const { sx, sy } = samplePatternPixel(
          fullX,
          fullY,
          pw,
          ph,
          scale,
          patternRotationDeg,
          patternFlipX,
          patternFlipY,
        );
        if (blendMode === 'threshold') {
          const gray = grayRaw + densityShift;
          let threshold = (pat[(sy * pw + sx) * 4] ?? 0) / 255;
          // Optional invert: flip pattern luminance so a white pattern
          // pixel ends up as a low threshold (= easy to keep white in
          // the output) and a black pattern pixel ends up high (= hard
          // to keep white). Matches the WYSIWYG mental model:
          // black = output black.
          if (invert) threshold = 1 - threshold;
          // Opacity: linearly interpolate between a flat 0.5 baseline
          // and the full pattern. opacity=0 silences the pattern (the
          // output becomes a 50%-density halftone driven by the source
          // alone); opacity=1 keeps the full pattern signal.
          if (opacity !== 1) {
            threshold = 0.5 + (threshold - 0.5) * opacity;
          }
          if (threshold < 0) threshold = 0;
          else if (threshold > 1) threshold = 1;
          if (thresholdNoiseAmt > 0) {
            const bnRaw = sampleBlueNoiseBilinear(fullX, fullY, noiseScale);
            const bn = (bnRaw + 0.5) / 256 - 0.5;
            threshold = Math.max(0, Math.min(1, threshold + thresholdNoiseAmt * bn));
          }
          if (outputTone) {
            const effective = clamp01(gray - threshold + 0.5);
            const tone = polarityWhite ? 1 - effective : effective;
            v = Math.round(tone * 255);
          } else {
            const isWhite = gray > threshold;
            const isWhitePixel = polarityWhite ? !isWhite : isWhite;
            v = isWhitePixel ? 255 : 0;
          }
        } else {
          // Compose path shared by multiply / screen / overlay /
          // softlight / pinlight: build an `effective` tone from
          // source + pattern via the chosen blend formula, then
          // dither to 1-bit through the same blue-noise step.
          let patternTone = (pat[(sy * pw + sx) * 4] ?? 0) / 255;
          // patternInvert applies before the compose formula so the uploaded
          // pattern can be flipped consistently across all blend modes.
          if (invert) {
            patternTone = 1 - patternTone;
          }
          if (opacity !== 1) {
            patternTone = 0.5 + (patternTone - 0.5) * opacity;
          }
          if (patternTone < 0) patternTone = 0;
          else if (patternTone > 1) patternTone = 1;

          let gray = grayRaw + densityShift;
          if (gray < 0) gray = 0;
          else if (gray > 1) gray = 1;

          let effective: number;
          switch (blendMode) {
            case 'screen':
              // Lighten group: pattern black = no-op, pattern white = wins.
              // Photoshop Screen: 1 - (1-a)(1-b).
              effective = 1 - (1 - gray) * (1 - patternTone);
              break;
            case 'overlay':
              // Contrast group, source-pivot: multiply on darks,
              // screen on lights, switching at source = 0.5.
              effective =
                gray < 0.5
                  ? 2 * gray * patternTone
                  : 1 - 2 * (1 - gray) * (1 - patternTone);
              break;
            case 'softlight':
              // Contrast group, soft. Pegtop variant (one-liner; visually
              // indistinguishable from the W3C piecewise formula).
              // Identity at patternTone = 0.5.
              effective = (1 - 2 * patternTone) * gray * gray + 2 * patternTone * gray;
              break;
            case 'pinlight':
              // Contrast group, pattern-pivot, two-sided clamp.
              // patternTone < 0.5 → darken-clamp; > 0.5 → lighten-clamp.
              effective =
                patternTone < 0.5
                  ? Math.min(gray, 2 * patternTone)
                  : Math.max(gray, 2 * patternTone - 1);
              break;
            case 'multiply':
            default:
              // Darken group.
              effective = gray * patternTone;
              break;
          }

          // Endpoint clamp: when the blend math saturates effective to
          // 0 or 1, short-circuit before the blue-noise dither. Without
          // this, blue-noise thresholds at the [0, 1] extremes can
          // occasionally fail the strict-`>` compare against a saturated
          // effective and leak black speckles into solid-white regions
          // (or vice versa) in Screen / Pin Light / etc.
          if (outputTone) {
            if (multiplyNoiseAmt > 0) {
              const bnRaw = sampleBlueNoiseBilinear(fullX, fullY, noiseScale) / 255;
              effective = clamp01(effective - (bnRaw - 0.5) * multiplyNoiseAmt);
            }
            const tone = polarityWhite ? 1 - effective : effective;
            v = Math.round(tone * 255);
          } else {
            let isWhite: boolean;
            if (effective >= 1) {
              isWhite = true;
            } else if (effective <= 0) {
              isWhite = false;
            } else {
              const bnRaw = sampleBlueNoiseBilinear(fullX, fullY, noiseScale) / 255;
              const threshold = 0.5 + (bnRaw - 0.5) * multiplyNoiseAmt;
              isWhite = effective > threshold;
            }
            const isWhitePixel = polarityWhite ? !isWhite : isWhite;
            v = isWhitePixel ? 255 : 0;
          }
        }
      }
      out.data[i] = v;
      out.data[i + 1] = v;
      out.data[i + 2] = v;
      out.data[i + 3] = 255;
    }
  }

  return out;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function samplePatternPixel(
  fullX: number,
  fullY: number,
  width: number,
  height: number,
  scale: number,
  rotationDeg: number,
  flipX: boolean,
  flipY: boolean,
): { sx: number; sy: number } {
  let localX = wrapPatternCoord(fullX / scale, width);
  let localY = wrapPatternCoord(fullY / scale, height);

  if (flipX) localX = width - 1 - localX;
  if (flipY) localY = height - 1 - localY;

  if (rotationDeg !== 0) {
    const cx = (width - 1) / 2;
    const cy = (height - 1) / 2;
    const rad = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = localX - cx;
    const dy = localY - cy;
    localX = cx + dx * cos - dy * sin;
    localY = cy + dx * sin + dy * cos;
  }

  return {
    sx: Math.floor(wrapPatternCoord(localX, width)),
    sy: Math.floor(wrapPatternCoord(localY, height)),
  };
}

function wrapPatternCoord(value: number, size: number): number {
  return ((value % size) + size) % size;
}
